/**
 * What a rep is offered when they reach a store (#3519).
 *
 * The rule used to live inline in two JSX conditions that disagreed about
 * whether the rep had clocked in. GPS check-in did not mention the day at all,
 * so it rendered whether or not there was an open clock window; the form that
 * collects a forgotten clock-in waited behind a Start Day tap that a rep
 * arriving at a store has no reason to look for.
 *
 * Prod is unambiguous about which one they took. Of 38 check-ins since go-live,
 * 30 had no clock-in earlier that day; all 27 clock-ins recorded
 * `retroactive: false`, meaning the backfill form was never used once; and not
 * one drive of 36 carried an end odometer, because the reading is only seeded
 * by starting the day. Reps tapped the button in front of them, every time.
 *
 * So the decision is stated once, here, where it can be tested.
 */

export type ArrivalOffer = 'gps_check_in' | 'start_day_backfill' | 'nothing';

export interface ArrivalState {
  /** Store visit status — only a pending visit is one the rep can start. */
  storeStatus: string;
  /** Day status from the leg chain: not_started | active | ended. */
  dayStatus: string;
  /** A manual or back-dated entry supplies its own times; it asks for nothing. */
  skipGpsCheck: boolean;
  /** A check-in already in flight. */
  isCheckingIn: boolean;
  /** The odometer prompt owns the screen while it is up. */
  showOdometerEntry: boolean;
  /** The multi-step start-day flow owns the screen while it is up. */
  showStartDayFlow: boolean;
}

/**
 * The backfill is offered ahead of GPS check-in, not instead of it: it is the
 * only place the rep can say when they left, what the odometer read at home,
 * and what it reads here — the three things no sensor can supply after the
 * fact. Skipping it still opens the day (see StoreDetail's onSkip), so the
 * check-in is never blocked, only better informed.
 */
export function arrivalOffer(state: ArrivalState): ArrivalOffer {
  if (state.storeStatus !== 'pending') return 'nothing';
  if (state.isCheckingIn) return 'nothing';
  if (state.skipGpsCheck) return 'nothing';

  if (state.dayStatus !== 'active') return 'start_day_backfill';

  if (state.showOdometerEntry || state.showStartDayFlow) return 'nothing';
  return 'gps_check_in';
}
